import { FC, useEffect, useRef, useState } from 'react';

import vrRequestModalImage from 'assets/vr_request_modal_image.png';

import {
    BottomSheet,
    BottomSheetWithImage,
    Button,
    Flex,
    ModalWithImage,
    NavBar,
    PhoneInput,
    Title,
    useBreakpoint,
} from '@hh.ru/magritte-ui';
import { CrossOutlinedSize24 } from '@hh.ru/magritte-ui/icon';

import { useRequestCancel } from 'hooks/common/useRequestCancel';
import useTrls from 'hooks/translation/useTrls';

import { fetchVrRequest } from 'components/VrCampaignBuilder/components/VrRequestModal/fetchVrRequest';

import {
    selectVrCampaignRequestModalVisible,
    setVrCampaignPhone,
    setVrCampaignRequestModalVisible,
} from 'store/models/vrCampaign';
import { useDispatch, useSelector } from 'store/utils';

const TrlKeys = {
    title: 'VrCampaignBuilderPage.requestModal.title',
    description: 'VrCampaignBuilderPage.requestModal.description',
    errorMessage: 'VrCampaignBuilderPage.requestModal.phone.error',
    submit: 'VrCampaignBuilderPage.requestModal.submit',
};

const COUNTRY_NAMES = {
    RU: 'Россия',
};

export const VrRequestModal: FC = () => {
    const [isValid, setIsValid] = useState(true);
    const [isLoading, setIsLoading] = useState(false);

    const requestModalVisible = useSelector(selectVrCampaignRequestModalVisible);
    const { phone, responses, budget, areas, requirements } = useSelector((state) => state.vrCampaign);

    const validRef = useRef(true);
    const trls = useTrls();
    const dispatch = useDispatch();
    const { isMobile } = useBreakpoint();
    const { abortRequest, abortSignal } = useRequestCancel();

    useEffect(() => () => abortRequest(), [abortRequest]);

    const handleCloseModal = () => {
        abortRequest();
        setIsLoading(false);
        dispatch([setVrCampaignRequestModalVisible(false), setVrCampaignPhone('')]);
        setIsValid(true);
    };

    const handleSubmit = () => {
        if (!validRef.current || !phone || isLoading) {
            return;
        }

        abortRequest();
        const signal = abortSignal();
        setIsLoading(true);

        void dispatch(
            fetchVrRequest(
                {
                    phone,
                    responses,
                    budget,
                    areas,
                    requirements,
                },
                handleCloseModal,
                signal
            )
        ).finally(() => {
            if (!signal.aborted) {
                setIsLoading(false);
            }
        });
    };

    const header = !isMobile && (
        <NavBar>
            <NavBar.Actions right={<CrossOutlinedSize24 onClick={handleCloseModal} />} />
        </NavBar>
    );

    const title = (
        <Title
            Element="h1"
            alignment={isMobile ? 'center' : 'left'}
            description={trls(TrlKeys.description)}
            descriptionStyle={isMobile ? 'secondary' : 'primary'}
        >
            {trls(TrlKeys.title)}
        </Title>
    );

    const content = (
        <PhoneInput
            lockedCountry="RU"
            onChange={(params) => {
                validRef.current = params.isValid || params.nationalNumber.length === 0;
                setIsValid(true);

                dispatch(setVrCampaignPhone(params.phone));
            }}
            countryNames={COUNTRY_NAMES}
            onBlur={() => {
                setIsValid(validRef.current);
            }}
            invalid={!isValid}
            errorMessage={trls(TrlKeys.errorMessage)}
            data-qa="vr-request-modal-phone"
        />
    );

    const footer = (
        <Flex align="center" justify="center" p={isMobile ? 16 : 12} pt={isMobile ? 32 : 16}>
            <Button
                mode="primary"
                style="accent"
                stretched
                loading={isLoading}
                onClick={handleSubmit}
                data-qa="vr-request-modal-submit"
            >
                {trls(TrlKeys.submit)}
            </Button>
        </Flex>
    );

    return (
        <>
            <ModalWithImage
                visible={requestModalVisible}
                onClose={handleCloseModal}
                size="medium"
                header={header}
                image={
                    <NavBar.Trigger asChild endTrigger>
                        <ModalWithImage.Media backgroundUrl={vrRequestModalImage} />
                    </NavBar.Trigger>
                }
                title={title}
                footer={footer}
                data-qa="vr-request-modal"
            >
                {content}
            </ModalWithImage>
            <BottomSheetWithImage
                visible={requestModalVisible}
                onClose={handleCloseModal}
                header={header}
                image={
                    <NavBar.Trigger asChild endTrigger>
                        <BottomSheet.Media backgroundUrl={vrRequestModalImage} />
                    </NavBar.Trigger>
                }
                title={title}
                footer={footer}
                data-qa="vr-request-modal-bottom-sheet"
            >
                {content}
            </BottomSheetWithImage>
        </>
    );
};
